import { merge } from 'lodash'
import { PatientHistoryType } from 'src/graphql.generated'
import { mapPatientAnswersToForm } from 'src/participantHistoryForm/form/intakeNoteComprehensive/initialValues/patientAnswersTransform'
import { useGetAdvancedIntakeNoteComprehensiveDataQuery } from './advancedIntakeNoteComprehensiveData.generated'
import { useGetAdvancedPatientHistoryPrefillQuery } from './advancedPatientHistoryPrefill.generated'
import { useGetAdvancedPrefilledIntakeNoteQuery } from './advancedPrefilledIntakeNote.generated'

type Prefill = { loading: boolean; value: Record<string, unknown> }

// A coach the server refuses (or any failure) gets an empty prefill and fills the form by hand.
export const usePatientHistoryPrefill = (uid: string, type: PatientHistoryType): Prefill => {
  const { data, loading } = useGetAdvancedPatientHistoryPrefillQuery({
    variables: { uid, type },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  return { loading, value: (data?.patientHistoryPrefillData?.value ?? {}) as Record<string, unknown> }
}

const numbered = (
  source: Record<string, unknown> | null | undefined,
  prefix: string,
  count: number,
  score: string
) => ({
  ...Object.fromEntries(
    Array.from({ length: count }, (_unused, index) => [
      `QUESTION${index + 1}`,
      source?.[`${prefix}_QUESTION_${index + 1}`] ?? undefined,
    ])
  ),
  TOTAL_SCORE: source?.[score] ?? undefined,
})

// Standard's `intakeNoteComprehensive/initialValues`.
export const useIntakeNoteComprehensivePrefill = (uid: string): Prefill => {
  const { data, loading } = useGetAdvancedIntakeNoteComprehensiveDataQuery({
    variables: { uid },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  const patientData = data?.intakeNoteComprehensivePatientData

  if (!patientData) {
    return { loading, value: {} }
  }

  const info = patientData.overallInformation
  const questionnaire = patientData.healthQuestionnaire

  return {
    loading,
    value: merge(
      {},
      info
        ? {
            FIRST_NAME: info.firstName ?? '',
            LAST_NAME: info.lastName ?? '',
            MEMBER_ID: info.memberID ?? '',
            CALL_START_TIME: info.callStartTime ?? '',
            CALL_END_TIME: info.callEndTime ?? '',
            PROVIDER_NAME: info.providerName ?? '',
            LICENSE_TYPE: info.licenseType ?? '',
          }
        : null,
      {
        REASON_FOR_SEEKING_TREATMENT: {
          HEALTH_QUESTIONNAIRE: {
            PHQ9: numbered(questionnaire?.phq9, 'PHQ9', 9, 'PHQ9_SCORE'),
            GAD7: numbered(questionnaire?.gad7, 'GAD7', 7, 'GAD7_SCORE'),
          },
        },
      },
      mapPatientAnswersToForm(patientData.patientAnswers?.data)
    ),
  }
}

export const useIntakeNotePrefill = (uid: string) => {
  const { data, loading } = useGetAdvancedPrefilledIntakeNoteQuery({
    variables: { uid },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  return { loading, prefill: data?.prefilledIntakeNote }
}
